// Live PM5 force curve: plots the raw CE06003D values exactly as they arrive
// (no smoothing, no stroke averaging). Each new drive clears and redraws.
import { useEffect, useRef, useState } from 'react';
import { getForceUnit } from '@/lib/forceUnit';

const LBS_TO_KG = 0.4536;
const N_TO_LBS = 1 / 4.44822;
const NEW_CURVE_GAP_MS = 250;

export function PM5ForceCurveLive() {
  const canvasRef = useRef(null);
  const curveRef = useRef({ pts: [], got: 0, total: 0, lastT: 0, lastSeq: -1 });
  const yMaxRef = useRef(0);
  const dirtyRef = useRef(true);
  const [unit, setUnit] = useState(getForceUnit);
  const [status, setStatus] = useState(() => {
    const s = window.__crewzForceStatus;
    return s && s.state !== 'listening' ? s.msg : 'Waiting for PM5 force data…';
  });
  const [peak, setPeak] = useState(null);
  const unitRef = useRef(unit);

  useEffect(() => {
    const toUnit = (lbs) => (unitRef.current === 'kg' ? lbs * LBS_TO_KG : lbs);
    const push = (lbsPoints, newCurve) => {
      const c = curveRef.current;
      if (newCurve) c.pts = [];
      c.pts.push(...lbsPoints);
      const pk = Math.max(...c.pts);
      yMaxRef.current = Math.max(yMaxRef.current, pk);
      setPeak(Math.round(toUnit(pk)));
      setStatus(null);
      dirtyRef.current = true;
    };
    const onPacket = (e) => {
      const { t, total, seq, points } = e.detail;
      const c = curveRef.current;
      const newCurve = t - c.lastT > NEW_CURVE_GAP_MS || c.got >= c.total || seq !== ((c.lastSeq + 1) & 0xff);
      if (newCurve) { c.got = 0; c.total = Math.max(1, total); }
      c.got += 1;
      c.lastT = t;
      c.lastSeq = seq;
      push(points, newCurve);
    };
    const onDemo = (e) => { if (e.detail?.demo) push(e.detail.points.map((n) => n * N_TO_LBS), true); };
    const onStatus = (e) => { if (e.detail?.state !== 'listening') setStatus(e.detail?.msg); };
    const onUnit = () => { unitRef.current = getForceUnit(); setUnit(unitRef.current); dirtyRef.current = true; };
    window.addEventListener('crewz-force-packet', onPacket);
    window.addEventListener('crewz-force-curve', onDemo);
    window.addEventListener('crewz-force-status', onStatus);
    window.addEventListener('crewz-force-unit-changed', onUnit);
    return () => {
      window.removeEventListener('crewz-force-packet', onPacket);
      window.removeEventListener('crewz-force-curve', onDemo);
      window.removeEventListener('crewz-force-status', onStatus);
      window.removeEventListener('crewz-force-unit-changed', onUnit);
    };
  }, []);

  useEffect(() => {
    let raf;
    const loop = () => {
      if (dirtyRef.current && canvasRef.current) {
        dirtyRef.current = false;
        draw(canvasRef.current, curveRef.current.pts, yMaxRef.current, unitRef.current);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="crewz-force-live" data-testid="pm5-force-live">
      <div className="crewz-force-live-head">
        <span>PM5 FORCE CURVE · LIVE</span>
        <span>PEAK <strong data-testid="pm5-force-peak">{peak ?? '—'}</strong> {unit.toUpperCase()}</span>
      </div>
      <div className="crewz-force-live-plot">
        <canvas ref={canvasRef} data-testid="pm5-force-canvas" />
        {status && <span className="crewz-force-live-status" data-testid="pm5-force-status">{status}</span>}
      </div>
      <style>{CSS}</style>
    </div>
  );
}

function draw(canvas, pts, yMaxLbs, unit) {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  const conv = unit === 'kg' ? LBS_TO_KG : 1;
  const yMax = Math.max(10, Math.ceil((yMaxLbs * conv * 1.1) / 10) * 10);
  const L = 34; const B = 18; const T = 6; const R = 6;
  const pw = w - L - R; const ph = h - T - B;
  ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
  ctx.fillStyle = '#6b7a88';
  ctx.strokeStyle = 'rgba(107,122,136,0.25)';
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i += 1) {
    const v = (yMax * i) / 4;
    const y = T + ph - (v / yMax) * ph;
    ctx.beginPath(); ctx.moveTo(L, y); ctx.lineTo(L + pw, y); ctx.stroke();
    ctx.textAlign = 'right'; ctx.fillText(String(Math.round(v)), L - 4, y + 3);
  }
  ctx.textAlign = 'left';
  ctx.fillText(unit, 2, T + 8);
  const n = Math.max(pts.length, 2);
  ctx.textAlign = 'center';
  ctx.fillText('0', L, h - 4);
  ctx.fillText(String(n - 1), L + pw, h - 4);
  ctx.fillText('sample', L + pw / 2, h - 4);
  if (!pts.length) return;
  ctx.strokeStyle = '#21B86A';
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  pts.forEach((v, i) => {
    const x = L + (i / (n - 1)) * pw;
    const y = T + ph - ((v * conv) / yMax) * ph;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();
}

const CSS = `
.crewz-force-live { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 160px; padding: 8px 10px; }
.crewz-force-live-head { display: flex; justify-content: space-between; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.12em; color: var(--c-dim, #6b7a88); margin-bottom: 4px; }
.crewz-force-live-head strong { color: var(--c-ink, #111); font-size: 13px; }
.crewz-force-live-plot { position: relative; flex: 1 1 auto; min-height: 130px; }
.crewz-force-live-plot canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.crewz-force-live-status { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; padding: 0 16px; font-size: 11px; letter-spacing: 0.08em; color: var(--c-dim, #6b7a88); pointer-events: none; }
`;
